/* ============================================================
   HEDDZ - theme behavior
   reveal animations, sticky header, faq accordion, smooth scroll
   buy box (real product data, selling-plan aware, add-to-cart -> drawer)
   Vanilla JS, no dependencies.

   The CTA adds to cart and opens the slide-out drawer (assets/cart-drawer.js).
   It no longer jumps straight to /checkout.

   FIRST-ORDER PRICING: never read from a preview/placeholder setting. It comes
   either from a real selling-plan intro tier (a plan with >1 price adjustment)
   or from the live plan price minus the first-order discount amount in the
   payload's `intro` block. If neither exists, no first-order price is shown.

   URL HANDLING: the buy box NEVER rewrites the path. An earlier version pushed
   the selected product's URL into the address bar on every render, which turned
   the homepage and /pages/six-reasons into /products/... and, worse, discarded
   every query parameter on the way, including utm_* tags. Now only the
   `intention` query param is touched, only on a product page, and always by
   mutating the current URL so other params survive.

   COPY RULE: no em dashes in any user-facing string in this file.
   ============================================================ */
(function () {
  'use strict';

  var routes = (window.Heddz && window.Heddz.routes) || {};
  var CART_ADD = routes.cartAdd || '/cart/add.js';
  var CHECKOUT_URL = '/checkout';
  var MONEY_FORMAT = (window.Heddz && window.Heddz.moneyFormat) || '${{amount}}';

  /* ---------- money (mirrors the formatter in cart-drawer.js) ---------- */
  function group(intPart, sep) {
    return intPart.replace(/(\d)(?=(\d{3})+$)/g, '$1' + sep);
  }
  function money(cents) {
    if (cents == null || isNaN(cents)) return '';
    var n = Math.abs(Math.round(cents));
    var whole = String(Math.floor(n / 100));
    var frac = String(n % 100);
    if (frac.length < 2) frac = '0' + frac;
    return MONEY_FORMAT.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, token) {
      switch (token) {
        case 'amount_no_decimals': return group(whole, ',');
        case 'amount_with_comma_separator': return group(whole, '.') + ',' + frac;
        case 'amount_no_decimals_with_comma_separator': return group(whole, '.');
        case 'amount_with_apostrophe_separator': return group(whole, "'") + '.' + frac;
        default: return group(whole, ',') + '.' + frac;
      }
    });
  }

  /* ---------- sticky header border on scroll ---------- */
  function initHeader() {
    var hdr = document.querySelector('[data-header]');
    if (!hdr) return;
    var onScroll = function () { hdr.classList.toggle('scrolled', window.scrollY > 8); };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- reveal-on-scroll (respects prefers-reduced-motion via CSS) ---------- */
  var revealIO = null;
  function showEl(el) { el.classList.add('in'); }
  function initReveal(scope) {
    var els = (scope || document).querySelectorAll('.reveal');
    if (!('IntersectionObserver' in window)) {
      els.forEach(showEl);
      return;
    }
    if (!revealIO) {
      revealIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { showEl(e.target); revealIO.unobserve(e.target); }
        });
      }, { threshold: 0.01, rootMargin: '0px 0px -6% 0px' });
    }
    els.forEach(function (el) {
      if (el.getBoundingClientRect().top < window.innerHeight * 0.92) showEl(el);
      else revealIO.observe(el);
    });
    // safety net, never leave content stuck hidden
    setTimeout(function () { els.forEach(showEl); }, 1800);
  }

  /* ---------- FAQ accordion (one open at a time, scoped per .faq group) ---------- */
  function initFaq(scope) {
    (scope || document).querySelectorAll('.faq__item').forEach(function (item) {
      if (item.dataset.faqBound) return;
      item.dataset.faqBound = '1';
      var q = item.querySelector('.faq__q');
      var a = item.querySelector('.faq__a');
      if (!q || !a) return;
      q.addEventListener('click', function () {
        var group = item.closest('.faq') || document;
        var open = item.classList.contains('open');
        group.querySelectorAll('.faq__item.open').forEach(function (o) {
          if (o !== item) {
            o.classList.remove('open');
            var oa = o.querySelector('.faq__a'); if (oa) oa.style.height = '0px';
            var oq = o.querySelector('.faq__q'); if (oq) oq.setAttribute('aria-expanded', 'false');
          }
        });
        if (open) {
          item.classList.remove('open'); a.style.height = '0px'; q.setAttribute('aria-expanded', 'false');
        } else {
          item.classList.add('open'); a.style.height = a.scrollHeight + 'px'; q.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  /* ---------- smooth scroll for in-page anchors ---------- */
  function initSmoothScroll() {
    document.addEventListener('click', function (e) {
      var a = e.target.closest('a[href^="#"], a[href*="#buy"], a[href*="#trio"], a[href*="#faq"]');
      if (!a) return;
      var hash = a.getAttribute('href');
      var id = hash.indexOf('#') > -1 ? hash.slice(hash.indexOf('#') + 1) : '';
      if (!id) return;
      var target = document.getElementById(id);
      // only intercept same-page anchors
      if (!target) return;
      var samePage = !a.pathname || a.pathname === window.location.pathname;
      if (!samePage) return;
      e.preventDefault();
      var top = target.getBoundingClientRect().top + window.scrollY - 72;
      window.scrollTo({ top: top, behavior: 'smooth' });
    });
  }

  /* ============================================================
     BUY BOX
     modes: 'single' (one pod, one-time only) | 'bundle' (trio / six pack,
     subscribe & save with a first-order price when one is configured).
     Prices resolve live product -> selling plans -> preview fallbacks.
     ============================================================ */
  function imgTag(media, w, eager) {
    return '<img class="img-fill" src="' + media.url + '" alt="' + (media.alt || '').replace(/"/g, '&quot;') +
      '" loading="' + (eager ? 'eager' : 'lazy') + '">';
  }
  function placeholder(text) {
    return '<div class="img-ph"><span>' + text + '</span></div>';
  }

  function mountBuyBox(root) {
    if (root.dataset.bbBound) return;
    root.dataset.bbBound = '1';

    var section = root.closest('[data-heddz-buybox]');
    if (!section) return;
    var dataEl = section.querySelector('script[data-buybox-json]');
    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (err) { return; }

    var products = data.products || {};
    var preview = data.preview || {};
    var setg = data.settings || {};
    var introCfg = data.intro || null;
    var $ = function (sel, ctx) { return (ctx || root).querySelector(sel); };
    var $$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || root).querySelectorAll(sel)); };

    var galMain = section.querySelector('[data-gal-main]');
    var galThumbs = section.querySelector('[data-gal-thumbs]');
    var matc = document.querySelector('[data-matc]');

    /* Only set on a real product page by sections/buy-box.liquid. Its absence is
       how we know not to touch the URL at all (homepage, /pages/*). */
    var currentHandle = root.getAttribute('data-current-handle');

    function roleOf(state) {
      return state.mode === 'bundle' ? state.bundle : state.intention;
    }
    function product(state) { return products[roleOf(state)] || {}; }

    /* Resolve a role's pricing from live data only.

       subMoney   - the recurring subscription price (what they pay ongoing)
       introMoney - the first-order price, when one is genuinely configured:
                    either a selling plan with >1 price adjustment, or the plan
                    price minus the first-order discount code amount.
       The preview `intro` setting is NEVER used as a price. Quoting a figure
       that no discount or plan backs is how you advertise $20 and charge $26.40. */
    function pricingFor(role) {
      var p = products[role] || {};
      var isPod = role === 'relief' || role === 'calm' || role === 'focus';
      var pv = preview[isPod ? 'single' : role] || {};
      var plans = p.plans || [];

      var base = null, introPlan = null;
      plans.forEach(function (pl) {
        if (pl.adjustments > 1) { if (!introPlan) introPlan = pl; }
        else if (!base) base = pl;
      });
      var chosen = introPlan || base;
      var live = plans.length > 0;

      var subCents = chosen
        ? (chosen.recurringCents != null ? chosen.recurringCents : null)
        : null;
      var subMoney = chosen
        ? (introPlan ? introPlan.recurringMoney : chosen.firstMoney)
        : pv.sub;

      var introCents = null;
      if (introPlan && introPlan.firstCents != null) {
        introCents = introPlan.firstCents;
      } else if (introCfg && introCfg.amountCents && introCfg.roles &&
                 introCfg.roles.indexOf(role) > -1 && subCents != null) {
        introCents = Math.max(0, subCents - introCfg.amountCents);
      }
      var hasIntro = introCents != null && subCents != null && introCents < subCents;

      return {
        live: live,
        onceMoney: (p.configured && p.priceMoney && p.price > 0) ? p.priceMoney : (pv.once || p.priceMoney),
        subMoney: subMoney,
        introMoney: hasIntro ? money(introCents) : null,
        hasIntro: hasIntro,
        subPlanId: chosen ? chosen.id : null
      };
    }

    // initial state from the server-computed values + ?intention override
    var params = new URLSearchParams(window.location.search);
    var qIntent = params.get('intention');
    var state = {
      mode: data.initial.mode || 'bundle',
      bundle: data.initial.bundle || 'trio',
      intention: data.initial.intention || 'relief',
      plan: 'sub'
    };
    if (state.mode === 'single' && ['relief', 'calm', 'focus'].indexOf(qIntent) > -1) {
      state.intention = qIntent;
    }

    /* Keep the chosen scent shareable WITHOUT ever changing the path or losing
       other query params. No-ops entirely off a product page. */
    function syncUrl() {
      if (!currentHandle) return;
      if (!window.history || !window.history.replaceState) return;
      try {
        var u = new URL(window.location.href);
        if (state.mode === 'single') u.searchParams.set('intention', state.intention);
        else u.searchParams.delete('intention');
        window.history.replaceState(null, '', u.pathname + u.search + u.hash);
      } catch (e) {}
    }

    /* The first-order price is shown whenever the data supports it. There is no
       longer anything for the shopper to tick. The legacy [data-intro] toggle is
       always hidden; leave enable_intro off in the templates. */
    function showIntro(pr) {
      return state.mode === 'bundle' && state.plan === 'sub' && pr.hasIntro;
    }

    function renderGallery(p) {
      if (!galMain) return;
      var media = p.media || [];
      if (media.length) {
        galMain.innerHTML = '<div class="sq">' + imgTag(media[0], 1200, true) + '</div>';
      } else {
        galMain.innerHTML = '<div class="sq">' + placeholder('product photo') + '</div>';
      }
      if (galThumbs) {
        if (media.length > 1) {
          galThumbs.hidden = false;
          var html = '';
          for (var i = 0; i < media.length && i < 4; i++) {
            html += '<button type="button" class="gal__thumb' + (i === 0 ? ' sel' : '') +
              '" data-gal-thumb="' + i + '" aria-label="view image ' + (i + 1) + '">' +
              '<div class="sq">' + imgTag(media[i], 400) + '</div></button>';
          }
          galThumbs.innerHTML = html;
          galThumbs.querySelectorAll('[data-gal-thumb]').forEach(function (btn) {
            btn.addEventListener('click', function () {
              var idx = parseInt(btn.getAttribute('data-gal-thumb'), 10);
              if (media[idx]) {
                galMain.innerHTML = '<div class="sq">' + imgTag(media[idx], 1200, true) + '</div>';
                galThumbs.querySelectorAll('.gal__thumb').forEach(function (b) { b.classList.remove('sel'); });
                btn.classList.add('sel');
              }
            });
          });
        } else {
          galThumbs.hidden = true;
          galThumbs.innerHTML = '';
        }
      }
    }

    function ctaPrice(pr) {
      if (state.mode === 'single') return pr.onceMoney;
      if (state.plan === 'once') return pr.onceMoney;
      return showIntro(pr) ? pr.introMoney : pr.subMoney;
    }

    /* One price bullet, led into the subscribe & save perks.
       Kept word-for-word identical to offerCopy() in cart-drawer.js. */
    function priceStory(pr) {
      if (pr.hasIntro && pr.introMoney && pr.subMoney) {
        return [pr.introMoney + ' on your first order (then ' + pr.subMoney + ' after)'];
      }
      if (pr.subMoney) {
        return [pr.subMoney + ' on every order'];
      }
      return [];
    }

    function render() {
      var role = roleOf(state);
      var p = product(state);
      var pr = pricingFor(role);

      // headline / name / bullets
      var hEl = $('[data-bb-headline]'); if (hEl) hEl.textContent = p.headline || '';
      var nEl = $('[data-bb-name]'); if (nEl) nEl.textContent = p.name || '';
      var bEl = $('[data-bb-bullets]');
      if (bEl) {
        bEl.innerHTML = (p.bullets || []).map(function (b) {
          return '<li><span class="ck">✓</span>' + b + '</li>';
        }).join('');
      }

      // tabs
      $$('.tab').forEach(function (t) {
        var on = t.getAttribute('data-mode') === state.mode;
        t.classList.toggle('active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });

      // bundle picker
      var bw = $('[data-bundle-wrap]');
      if (bw) bw.style.display = state.mode === 'bundle' ? '' : 'none';
      $$('.bundle__btn').forEach(function (b) {
        b.classList.toggle('sel', b.getAttribute('data-b') === state.bundle);
      });
      var bpT = $('[data-bb-bprice-trio]'); if (bpT) bpT.textContent = pricingFor('trio').onceMoney || '';
      var bpD = $('[data-bb-bprice-double]'); if (bpD) bpD.textContent = pricingFor('double').onceMoney || '';

      // intention picker + single one-time price row
      var iw = $('[data-intent-wrap]');
      if (iw) iw.style.display = state.mode === 'single' ? '' : 'none';
      $$('.intent__btn').forEach(function (b) {
        b.classList.toggle('sel', b.getAttribute('data-k') === state.intention);
      });
      if (state.mode === 'single') {
        var sp = $('[data-bb-singleprice]'); if (sp) sp.textContent = pr.onceMoney || '';
      }

      // plans, bundles only; singles are strictly one-time
      var plansEl = $('[data-plans]');
      if (plansEl) {
        plansEl.hidden = state.mode !== 'bundle';
        if (state.mode === 'bundle') {
          var intro = showIntro(pr);
          var subnow = $('[data-bb-subnow]');
          if (subnow) subnow.textContent = (intro ? pr.introMoney : pr.subMoney) || '';
          var subwas = $('[data-bb-subwas]'); if (subwas) subwas.textContent = pr.onceMoney || '';
          var oncenow = $('[data-bb-oncenow]'); if (oncenow) oncenow.textContent = pr.onceMoney || '';
          $$('.plan').forEach(function (pl) {
            pl.classList.toggle('sel', pl.getAttribute('data-plan') === state.plan);
          });

          // perks per bundle, led by the price bullet when subscribing
          var perksEl = $('[data-bb-perks]');
          if (perksEl) {
            var perksTxt = state.bundle === 'double' ? setg.doublePerks : setg.trioPerks;
            var list = (perksTxt || '').split('\n').map(function (pk) { return pk.trim(); })
              .filter(function (pk) { return !!pk; });
            if (state.plan === 'sub') list = priceStory(pr).concat(list);
            perksEl.innerHTML = list.map(function (pk) { return '<li>' + pk + '</li>'; }).join('');
          }

          /* The old first-order toggle is never shown. Its price came from a
             preview setting with no discount behind it, which misquoted the
             first order. First-order pricing is now automatic and priced from
             live data above. */
          var introEl = $('[data-intro]');
          if (introEl) introEl.hidden = true;
        }
      }

      // cta price
      var price = ctaPrice(pr);
      var cp = $('[data-bb-cta-price]'); if (cp) cp.textContent = price || '';

      // mobile sticky bar
      if (matc) {
        var mp = matc.querySelector('[data-matc-price]'); if (mp) mp.textContent = price || '';
        var ml = matc.querySelector('[data-matc-label]');
        if (ml) {
          var planName = (state.mode === 'bundle' && state.plan === 'sub') ? setg.subscribeName : setg.onceName;
          var sel = state.mode === 'bundle'
            ? (setg.bundleNames && setg.bundleNames[state.bundle]) || state.bundle
            : (setg.intentNames && setg.intentNames[state.intention]) || state.intention;
          ml.textContent = planName + ' · ' + sel;
        }
      }

      // gallery
      renderGallery(p);

      // scent in the query string, product pages only, path untouched
      syncUrl();
    }

    // events
    $$('.tab').forEach(function (t) {
      t.addEventListener('click', function () {
        state.mode = t.getAttribute('data-mode');
        if (state.mode === 'bundle' && state.plan !== 'once') state.plan = 'sub';
        render();
      });
    });
    $$('.bundle__btn').forEach(function (b) {
      b.addEventListener('click', function () {
        state.bundle = b.getAttribute('data-b');
        render();
      });
    });
    $$('.intent__btn').forEach(function (b) {
      b.addEventListener('click', function () { state.intention = b.getAttribute('data-k'); render(); });
    });
    $$('.plan').forEach(function (pl) {
      pl.addEventListener('click', function (e) {
        e.preventDefault();
        state.plan = pl.getAttribute('data-plan');
        render();
      });
    });

    // add to cart -> drawer
    var ctaBtn = $('[data-bb-cta]');
    var ctaLabel = $('[data-bb-cta-label]');
    var errEl = $('[data-bb-err]');
    var matcBtn = matc ? matc.querySelector('[data-matc-cta]') : null;

    function setLoading(on) {
      [ctaBtn, matcBtn].forEach(function (b) { if (b) b.disabled = on; });
      if (ctaLabel) ctaLabel.textContent = on ? (setg.ctaLoading || 'adding…') : (setg.ctaLabel || 'add to cart');
    }
    function showErr(msg) {
      if (!errEl) return;
      errEl.textContent = msg;
      errEl.hidden = false;
    }
    function clearErr() { if (errEl) errEl.hidden = true; }

    function addToCart() {
      clearErr();
      var p = product(state);
      var pr = pricingFor(roleOf(state));
      if (!p.variantId) {
        showErr('this option isn’t available yet. please check back soon.');
        return;
      }
      var item = { id: p.variantId, quantity: 1 };
      // subscriptions only exist on bundles
      if (state.mode === 'bundle' && state.plan === 'sub' && pr.live && pr.subPlanId) {
        item.selling_plan = pr.subPlanId;
      }
      setLoading(true);

      var drawer = window.Heddz && window.Heddz.cartDrawer;
      if (drawer && drawer.addAndOpen) {
        drawer.addAndOpen(item)
          .then(function () { setLoading(false); })
          .catch(function () { setLoading(false); });
        return;
      }

      // fallback if the drawer asset failed to load, never trap the shopper
      fetch(CART_ADD, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [item] })
      })
        .then(function (r) { return r.ok ? r.json() : r.json().then(function (e) { throw e; }); })
        .then(function () { window.location.href = CHECKOUT_URL; })
        .catch(function (e) {
          setLoading(false);
          showErr((e && e.description) || 'sorry, we couldn’t add that. please try again.');
        });
    }

    if (ctaBtn) ctaBtn.addEventListener('click', addToCart);
    if (matcBtn) matcBtn.addEventListener('click', addToCart);

    // mobile bar visibility (only relevant when present / under 940px handled by CSS class)
    if (matc) {
      matc.classList.add('is-on');
      document.body.classList.add('has-matc');
    }

    render();
  }

  function initBuyBoxes(scope) {
    (scope || document).querySelectorAll('[data-buybox]').forEach(mountBuyBox);
  }

  /* ---------- boot ---------- */
  function boot(scope) {
    initReveal(scope);
    initFaq(scope);
    initBuyBoxes(scope);
  }

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  ready(function () {
    initHeader();
    initSmoothScroll();
    boot(document);
  });

  // Theme editor, re-init sections rendered/loaded in the editor
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:select', function (e) { boot(e.target); });
})();
